import { apiClient, ApiResponse } from './api'

export interface User {
  _id: string
  email: string
  firstName: string
  lastName: string
  phone?: string
  password?: string
  roles: Array<{
    _id: string
    name: string
  }>
  status?: 'ACTIVE' | 'INACTIVE' | 'PENDING' | 'SUSPENDED'
  tenantId: string
  createdAt: string
  createdBy: string | null
  updatedAt: string
  updatedBy: string | null
  deletedAt: string | null
  deletedBy: string | null
  __v: number
}

export interface UsersResponse {
  success: boolean
  message: string
  data: User[]
}

export interface UsersPaginatedResponse {
  success: boolean
  message: string
  data: {
    data: User[]
    total: number
    page: number
    limit: number
  }
}

export interface GetUsersParams {
  page?: number
  limit?: number
  sort?: 'asc' | 'desc'
  sortBy?: string
  search?: string
}

export interface CreateUserRequest {
  email: string
  firstName: string
  lastName: string
  phone: string
  roles: string[]
  tenantId: string
}

export interface UpdateUserRequest {
  firstName?: string
  lastName?: string
  roles?: string[]
  status?: 'ACTIVE' | 'INACTIVE' | 'PENDING' | 'SUSPENDED'
}

export const usersApi = {
  /**
   * Get users by tenant ID
   */
  async getUsersByTenant(tenantId: string): Promise<ApiResponse<User[]>> {
    try {
      const response = await apiClient.get<User[]>(`/auth-service/api/v1/users/tenant/${tenantId}`, undefined, 'AUTH')
      return response
    } catch (error) {
      console.error('Get users by tenant error:', error)
      throw error
    }
  },

  /**
   * Get all users with pagination and sorting
   */
  async getUsers(params: GetUsersParams = {}): Promise<ApiResponse<User[]>> {
    try {
      const queryParams = new URLSearchParams()
      
      if (params.page) queryParams.append('page', params.page.toString())
      if (params.limit) queryParams.append('limit', params.limit.toString())
      if (params.sort) queryParams.append('sort', params.sort)
      if (params.sortBy) queryParams.append('sortBy', params.sortBy)
      if (params.search) queryParams.append('search', params.search)
      
      const queryString = queryParams.toString()
      const url = queryString ? `/auth-service/api/v1/users?${queryString}` : '/auth-service/api/v1/users'
      
      const response = await apiClient.get<UsersPaginatedResponse>(url, undefined, 'AUTH')
      console.log('Raw API response:', response)
      
      // Handle both paginated and non-paginated responses
      let usersData: User[] = []
      if (response.data) {
        if (Array.isArray(response.data)) {
          // Direct array response
          usersData = response.data
        } else if (response.data.data && Array.isArray(response.data.data)) {
          // Paginated response with nested data
          usersData = response.data.data
        }
      }
      
      console.log('Processed users data:', usersData)
      
      return {
        success: response.success,
        message: response.message,
        data: usersData
      }
    } catch (error) {
      console.error('Get users error:', error)
      throw error
    }
  },

  /**
   * Get user by ID
   */
  async getUserById(userId: string): Promise<ApiResponse<User>> {
    try {
      const response = await apiClient.get<User>(`/auth-service/api/v1/users/${userId}`, undefined, 'AUTH')
      return response
    } catch (error) {
      console.error('Get user by ID error:', error)
      throw error
    }
  },

  /**
   * Create a new user
   */
  async createUser(userData: CreateUserRequest): Promise<ApiResponse<User>> {
    try {
      const response = await apiClient.post<User>('/auth-service/api/v1/users', userData, undefined, 'AUTH')
      return response
    } catch (error) {
      console.error('Create user error:', error)
      throw error
    }
  },

  /**
   * Update user
   */
  async updateUser(userId: string, userData: UpdateUserRequest): Promise<ApiResponse<User>> {
    try {
      const response = await apiClient.patch<User>(`/auth-service/api/v1/users/${userId}`, userData, undefined, 'AUTH')
      return response
    } catch (error) {
      console.error('Update user error:', error)
      throw error
    }
  },

  /**
   * Delete user
   */
  async deleteUser(userId: string): Promise<ApiResponse<{ success: boolean; message: string }>> {
    try {
      const response = await apiClient.delete<{ success: boolean; message: string }>(`/auth-service/api/v1/users/${userId}`, undefined, 'AUTH')
      return response
    } catch (error) {
      console.error('Delete user error:', error)
      throw error
    }
  },

  /**
   * Get all pentesters (users with PENTESTER role)
   */
  async getPentesters(): Promise<ApiResponse<User[]>> {
    try {
      const response = await apiClient.get<UsersPaginatedResponse>('/auth-service/api/v1/users?role=PENTESTER', undefined, 'AUTH')
      if (response.success && response.data) {
        return {
          success: response.success,
          message: response.message,
          data: (response.data as any).data || []
        }
      }
      return {
        success: false,
        message: 'Failed to fetch pentesters',
        data: []
      }
    } catch (error) {
      console.error('Get pentesters error:', error)
      throw error
    }
  }
}
